import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import mixpanel from "mixpanel-browser";
import React, { useCallback, useState } from "react";

import { ExportDataQuery } from "../../../../../__generated__/graphql";
import Alert from "../../../../../shared/components/design-system/Alert";
import Button from "../../../../../shared/components/design-system/Button";
import Dialog from "../../../../../shared/components/design-system/Dialog";
import {
  SectionHeader,
  StandardFormFooter,
} from "../../../../../shared/components/design-system/component-groups/section-header-footer";
import { ContainerSize } from "../../design-system/Container";
import { ExportDownloads } from "../../design-system/ExportDownloads";

export const EXPORT_DATA_QUERY = gql`
  query exportData {
    dataExport {
      password
    }
  }
`;

export default function ExportAllDataButton() {
  const [exportRequested, setExportRequested] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { data, loading, error } = useQuery<ExportDataQuery>(EXPORT_DATA_QUERY, {
    skip: !exportRequested,
    fetchPolicy: "no-cache",
  });

  const openModal = useCallback(() => {
    mixpanel.track("Opened all-data export modal");
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    mixpanel.track("Closed all-data export modal");
    setModalOpen(false);
    setExportRequested(false);
  }, []);

  const startExport = useCallback(() => {
    mixpanel.track("Started all-data export");
    setExportRequested(true);
  }, []);

  return (
    <>
      <Button variant="secondary" onClick={openModal} label="Export all data" />
      <Dialog size={ContainerSize.Small} isOpen={modalOpen} onClose={closeModal} isLoading={loading}>
        <SectionHeader title="Export all data" />
        {!exportRequested || loading ? (
          <p>
            You’ll receive an email with a download link once the data is ready. The link is valid for 24 hours. The
            file is password-protected, and the password will be shown on the next screen.
          </p>
        ) : error ? (
          // TODO: translate error messages
          <Alert variant="danger" title="An error occurred while starting the export." message={error.message} />
        ) : (
          exportRequested && (
            <>
              <ExportDownloads fullDataExport password={data?.dataExport.password ?? ""} showZipInstructions />
            </>
          )
        )}
        <StandardFormFooter>
          {!exportRequested ? <Button label="Cancel" onClick={closeModal} variant="secondary" /> : null}
          <Button
            variant="primary"
            onClick={!exportRequested || loading ? startExport : closeModal}
            loading={!exportRequested && loading}
            label={!exportRequested || loading ? "Export all data" : "Done"}
          />
        </StandardFormFooter>
      </Dialog>
    </>
  );
}
